import BlockedPage from '~/common/components/BlockedPage';
import {
  type NavFeatureFlags,
  useNavFeatureFlags,
} from '~/common/hooks/useNavFeatureFlags';

/**
 * Higher-order component to conditionally render a layout based on feature flags.
 * @param featureFlag The feature flag to check.
 * @param LayoutComponent The layout component to render if the feature flag is enabled.
 * @returns A wrapped component that either renders the layout or a blocked page.
 */
export function withFeatureFlagBlock<TProps>(
  featureFlag: keyof NavFeatureFlags,
  LayoutComponent: React.ComponentType<TProps>,
) {
  const displayName =
    LayoutComponent.displayName || LayoutComponent.name || 'Component';

  function WrappedComponent(props: Readonly<TProps>) {
    const flagValue = useNavFeatureFlags()[featureFlag];

    if (!flagValue) {
      return <BlockedPage />;
    }

    return <LayoutComponent {...props} />;
  }

  WrappedComponent.displayName = `withFeatureFlagBlock(${displayName})`;

  return WrappedComponent;
}
